import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/SettingsContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Mail, Trash2, Eye, Save, Upload } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["baru", "diverifikasi", "diproses", "diterima", "tidak_diterima", "daftar_ulang"];
const STATUS_COLORS = {
  baru: "bg-slate-100 text-slate-700",
  diverifikasi: "bg-blue-100 text-blue-700",
  diproses: "bg-amber-100 text-amber-800",
  diterima: "bg-green-100 text-green-700",
  tidak_diterima: "bg-red-100 text-red-700",
  daftar_ulang: "bg-purple-100 text-purple-700",
};

export function PPDBManage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [newStatus, setNewStatus] = useState("baru");
  const [note, setNote] = useState("");

  const load = async () => {
    const p = new URLSearchParams({ limit: "100" });
    if (search) p.set("search", search);
    if (status) p.set("status", status);
    const { data } = await api.get(`/ppdb/applicants?${p}`);
    setItems(data.items);
  };
  useEffect(() => { load(); }, [status]);

  const openDetail = (a) => { setSel(a); setNewStatus(a.status); setNote(a.note || ""); };
  const saveStatus = async () => {
    try {
      await api.patch(`/ppdb/applicants/${sel.id}/status`, { status: newStatus, note });
      toast.success("Status diperbarui"); setSel(null); load();
    } catch { toast.error("Gagal memperbarui"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus pendaftar?")) return;
    try { await api.delete(`/ppdb/applicants/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };
  const exportCSV = () => {
    const headers = ["Reg No", "Nama", "NISN", "Program", "Status", "Email", "Telepon", "Tanggal Daftar"];
    const rows = items.map((a) => [a.registration_number, a.full_name, a.nisn, a.program_choice, a.status, a.email, a.phone, a.created_at]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ppdb-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-ppdb-page">
      <h1 className="font-serif text-3xl font-bold text-[#0B1D3A]">Pendaftar PPDB</h1>
      <p className="text-slate-500 mt-1">Kelola & verifikasi pendaftar.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Input data-testid="ppdb-search" placeholder="Cari nama / NISN / nomor" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-xs" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ppdb-filter-status" className="w-56"><SelectValue placeholder="Semua Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="ppdb-export" onClick={exportCSV} variant="outline"><Upload className="w-4 h-4 mr-1 rotate-180" /> Export CSV</Button>
      </div>

      <div className="mt-5 card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Reg No</th><th className="px-4 py-3">Nama</th><th className="px-4 py-3">NISN</th><th className="px-4 py-3">Program</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`ppdb-row-${a.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs">{a.registration_number}</td>
                <td className="px-4 py-3 font-semibold text-[#0B1D3A]">{a.full_name}</td>
                <td className="px-4 py-3">{a.nisn}</td>
                <td className="px-4 py-3">{a.program_choice}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[a.status]}`}>{a.status.replace("_", " ")}</span></td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`ppdb-view-${a.id}`} onClick={() => openDetail(a)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-blue-50 text-blue-600"><Eye className="w-4 h-4" /></button>
                  <button data-testid={`ppdb-delete-${a.id}`} onClick={() => remove(a.id)} className="ml-1 w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Belum ada pendaftar.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Pendaftar</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                {[["Nomor Pendaftaran", sel.registration_number], ["Nama", sel.full_name], ["NISN", sel.nisn], ["Jenis Kelamin", sel.gender], ["Tempat Lahir", sel.birth_place], ["Tanggal Lahir", sel.birth_date], ["Asal Sekolah", sel.previous_school], ["Program", sel.program_choice], ["Orang Tua", sel.parent_name], ["Telepon", sel.phone], ["Email", sel.email]].map(([k, v]) => (
                  <div key={k}><div className="text-xs text-slate-500 uppercase tracking-wider">{k}</div><div className="font-semibold text-[#0B1D3A]">{v || "-"}</div></div>
                ))}
              </div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider">Alamat</div><div className="font-semibold">{sel.address}</div></div>
              <div className="border-t border-slate-200 pt-4">
                <Label>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="ppdb-detail-status" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                <Label className="mt-3 block">Catatan</Label>
                <Textarea data-testid="ppdb-detail-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)}>Tutup</Button>
            <Button data-testid="ppdb-save-status" onClick={saveStatus} className="bg-[#0B1D3A] text-white"><Save className="w-4 h-4 mr-1" /> Simpan Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MessagesManage() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/contact/messages?limit=100"); setItems(data.items); };
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Hapus pesan?")) return; await api.delete(`/contact/messages/${id}`); toast.success("Terhapus"); load(); };
  return (
    <div data-testid="admin-messages-page">
      <h1 className="font-serif text-3xl font-bold text-[#0B1D3A]">Pesan Kontak</h1>
      <div className="mt-6 grid gap-4">
        {items.map((m) => (
          <div key={m.id} className="card-elevated p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString("id-ID")}</div>
                <h3 className="font-serif font-bold text-lg text-[#0B1D3A] mt-1">{m.subject}</h3>
                <div className="text-sm text-slate-600 mt-1">dari <span className="font-semibold">{m.name}</span> • {m.email} {m.phone ? `• ${m.phone}` : ""}</div>
              </div>
              <button data-testid={`msg-delete-${m.id}`} onClick={() => remove(m.id)} className="w-9 h-9 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="mt-3 text-slate-700">{m.message}</p>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 text-center py-10">Belum ada pesan masuk.</div>}
      </div>
    </div>
  );
}

export function SettingsManage() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (settings) setForm({ ...settings, mission: (settings.mission || []).join("\n") }); }, [settings]);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, mission: (form.mission || "").split("\n").map((s) => s.trim()).filter(Boolean) };
      delete payload.id; delete payload.updated_at;
      await api.put("/settings", payload);
      toast.success("Pengaturan tersimpan");
      refresh();
    } catch { toast.error("Gagal menyimpan"); } finally { setLoading(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, logo_url: data.url });
      toast.success("Logo diunggah");
    } catch { toast.error("Gagal unggah"); }
  };

  const uploadImageTo = (fieldKey, successMsg) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, [fieldKey]: data.url }));
      toast.success(successMsg);
    } catch { toast.error("Gagal unggah"); }
  };

  const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : (process.env.REACT_APP_BACKEND_URL + u));

  const fields = [
    ["name", "Nama Sekolah"], ["tagline", "Tagline"],
    ["address", "Alamat", "textarea"],
    ["phone", "Telepon"], ["whatsapp", "WhatsApp"], ["email", "Email"],
    ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"], ["tiktok", "TikTok URL"],
    ["maps_url", "Google Maps Embed URL"],
    ["principal_name", "Nama Kepala Sekolah"],
    ["principal_photo_url", "Foto Kepala Sekolah URL"],
    ["principal_message", "Sambutan Kepala Sekolah", "textarea"],
    ["vision", "Visi"],
    ["mission", "Misi (satu baris per poin)", "textarea"],
  ];

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-serif text-3xl font-bold text-[#0B1D3A]">Pengaturan Sekolah</h1>
      <p className="text-slate-500 mt-1">Semua konten dinamis yang ditampilkan di website publik.</p>

      <div className="mt-6 card-elevated p-6">
        <Label>Logo Sekolah</Label>
        <div className="mt-2 flex items-center gap-5">
          {form.logo_url && <img src={form.logo_url.startsWith("http") ? form.logo_url : (process.env.REACT_APP_BACKEND_URL + form.logo_url)} alt="logo" className="w-16 h-16 object-contain rounded border border-slate-200" />}
          <label className="px-4 py-2 border border-slate-300 rounded-lg cursor-pointer text-sm hover:bg-slate-50 inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Pilih logo<input type="file" className="hidden" onChange={uploadLogo} accept="image/*" /></label>
        </div>
      </div>

      <div className="mt-6 card-elevated p-6 grid md:grid-cols-2 gap-5">
        {fields.map(([k, l, type]) => (
          <div key={k} className={type === "textarea" ? "md:col-span-2" : ""}>
            <Label className="text-xs font-semibold">{l}</Label>
            {type === "textarea" ? (
              <Textarea data-testid={`settings-${k}`} rows={k === "mission" ? 5 : 3} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
            ) : (
              <Input data-testid={`settings-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-5 flex justify-end">
        <Button data-testid="settings-save" onClick={save} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-[#0B1D3A] font-bold h-11 px-8"><Save className="w-4 h-4 mr-1" /> {loading ? "Menyimpan..." : "Simpan Pengaturan"}</Button>
      </div>
    </div>
  );
}
